import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";

const KEY = "vmr_cart_v1";
const CartCtx = createContext({ ids: [], items: [], subtotal: 0, add: () => {}, remove: () => {}, clear: () => {}, has: () => false });

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "number") : [];
  } catch (error) {
    console.warn("Cart: failed to read localStorage", error);
    return [];
  }
}

export function CartProvider({ children }) {
  const [ids, setIds] = useState(read);
  const [items, setItems] = useState([]);
  const [subtotal, setSubtotal] = useState(0);

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(ids));
    } catch (error) {
      console.warn("Cart: failed to save", error);
    }
  }, [ids]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === KEY) setIds(read());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const resolve = useCallback(async () => {
    if (ids.length === 0) {
      setItems([]); setSubtotal(0); return;
    }
    try {
      const { data } = await api.post("/cart/resolve", { listing_ids: ids });
      setItems(data.items || []);
      setSubtotal(data.subtotal || 0);
      if ((data.unavailable || []).length > 0) {
        setIds((prev) => prev.filter((x) => !data.unavailable.includes(x)));
      }
    } catch (error) {
      console.warn("Cart resolve failed", error);
      setItems([]); setSubtotal(0);
    }
  }, [ids]);

  useEffect(() => { resolve(); }, [resolve]);

  const add = useCallback((id) => {
    const num = Number(id);
    if (!num) return;
    setIds((prev) => (prev.includes(num) ? prev : [num, ...prev]));
  }, []);

  const remove = useCallback((id) => {
    const num = Number(id);
    setIds((prev) => prev.filter((x) => x !== num));
  }, []);

  const has = useCallback((id) => ids.includes(Number(id)), [ids]);
  const clear = useCallback(() => setIds([]), []);

  const value = useMemo(
    () => ({ ids, items, subtotal, add, remove, clear, has, refresh: resolve }),
    [ids, items, subtotal, add, remove, clear, has, resolve]
  );

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export function useCart() {
  return useContext(CartCtx);
}
